import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  AdminGeneralSettings, 
  AdminGeneralSettingsCreateDto, 
  AdminGeneralSettingsUpdateDto,
  AdminGeneralSettingsResponse,
  AdminGeneralSettingsLatestResponse
} from "../../modules/shared/model";
import { 
  getLatestAdminGeneralSettings as getLatestAdminGeneralSettingsAction,
  getAdminGeneralSettings as getAdminGeneralSettingsAction, 
  getAdminGeneralSettingsById as getAdminGeneralSettingsByIdAction,
  createAdminGeneralSettings as createAdminGeneralSettingsAction,
  updateAdminGeneralSettings as updateAdminGeneralSettingsAction,
  deleteAdminGeneralSettings as deleteAdminGeneralSettingsAction
} from "../Actions/AdminGeneralSettingsActions";

// Async Thunks
export const fetchLatestAdminGeneralSettings = createAsyncThunk("adminGeneralSettings/fetchLatest", async (_, { rejectWithValue }) => {
  try { 
    return await getLatestAdminGeneralSettingsAction(); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch latest admin general settings"); 
  }
});

export const fetchAdminGeneralSettings = createAsyncThunk("adminGeneralSettings/fetchAll", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { 
    return await getAdminGeneralSettingsAction(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch admin general settings"); 
  }
});

export const fetchAdminGeneralSettingsById = createAsyncThunk("adminGeneralSettings/fetchById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getAdminGeneralSettingsByIdAction(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch admin general settings"); 
  }
});

export const createAdminGeneralSettings = createAsyncThunk("adminGeneralSettings/create", async (data: AdminGeneralSettingsCreateDto, { rejectWithValue }) => {
  try { 
    return await createAdminGeneralSettingsAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create admin general settings"); 
  }
});

export const updateAdminGeneralSettings = createAsyncThunk("adminGeneralSettings/update", async ({ id, data }: { id: string; data: AdminGeneralSettingsUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updateAdminGeneralSettingsAction(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update admin general settings"); 
  }
});

export const deleteAdminGeneralSettings = createAsyncThunk("adminGeneralSettings/delete", async (id: string, { rejectWithValue }) => {
  try { 
    await deleteAdminGeneralSettingsAction(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete admin general settings"); 
  }
});

// State interface
interface AdminGeneralSettingsState {
  // Latest settings (single item)
  latestSettings: AdminGeneralSettings | null;
  latestSettingsLoading: boolean;
  latestSettingsError: string | null;
  
  // All settings (paginated)
  settings: AdminGeneralSettings[];
  currentSettings: AdminGeneralSettings | null;
  settingsLoading: boolean;
  settingsError: string | null;
  settingsTotalCount?: number;
  settingsCurrentPage: number;
  settingsHasNextPage: boolean;
  settingsHasPreviousPage: boolean;
  settingsSearchQuery: string;
  
  // Form state
  isFormDirty: boolean;
  lastSavedAt: string | null;
}

const initialState: AdminGeneralSettingsState = {
  // Latest settings
  latestSettings: null,
  latestSettingsLoading: false,
  latestSettingsError: null,
  
  // All settings
  settings: [],
  currentSettings: null,
  settingsLoading: false,
  settingsError: null,
  settingsTotalCount: 0,
  settingsCurrentPage: 1,
  settingsHasNextPage: false,
  settingsHasPreviousPage: false,
  settingsSearchQuery: "",
  
  // Form state
  isFormDirty: false,
  lastSavedAt: null,
};

const adminGeneralSettingsSlice = createSlice({
  name: "adminGeneralSettings",
  initialState,
  reducers: {
    clearLatestSettingsError(state) { 
      state.latestSettingsError = null; 
    },
    clearSettingsError(state) { 
      state.settingsError = null; 
    },
    clearCurrentSettings(state) { 
      state.currentSettings = null; 
    },
    setSettingsSearchQuery(state, action: PayloadAction<string>) { 
      state.settingsSearchQuery = action.payload; 
    },
    setFormDirty(state, action: PayloadAction<boolean>) { 
      state.isFormDirty = action.payload; 
    },
    clearAllSettings(state) {
      state.settings = [];
      state.currentSettings = null;
      state.latestSettings = null;
      state.settingsTotalCount = 0;
    },
    updateLatestSettingsFromForm(state, action: PayloadAction<Partial<AdminGeneralSettings>>) {
      if (state.latestSettings) {
        state.latestSettings = { ...state.latestSettings, ...action.payload };
      }
    }
  },
  extraReducers: (builder) => {
    // Latest Settings Extra Reducers
    builder
      .addCase(fetchLatestAdminGeneralSettings.pending, (state) => { 
        state.latestSettingsLoading = true; 
        state.latestSettingsError = null; 
      })
      .addCase(fetchLatestAdminGeneralSettings.fulfilled, (state, action: PayloadAction<AdminGeneralSettingsLatestResponse>) => { 
        state.latestSettingsLoading = false; 
        state.latestSettings = action.payload; 
        state.lastSavedAt = new Date().toISOString();
      })
      .addCase(fetchLatestAdminGeneralSettings.rejected, (state, action: any) => { 
        state.latestSettingsLoading = false; 
        state.latestSettingsError = action.payload as string; 
      });

    // All Settings Extra Reducers
    builder
      .addCase(fetchAdminGeneralSettings.pending, (state) => { 
        state.settingsLoading = true; 
        state.settingsError = null; 
      })
      .addCase(fetchAdminGeneralSettings.fulfilled, (state, action: PayloadAction<AdminGeneralSettingsResponse>) => { 
        state.settingsLoading = false; 
        state.settings = action.payload.data || []; 
        state.settingsTotalCount = action.payload.totalCount || 0; 
        state.settingsCurrentPage = action.payload.page || 1;
        state.settingsHasNextPage = action.payload.hasNextPage || false;
        state.settingsHasPreviousPage = action.payload.hasPreviousPage || false;
      })
      .addCase(fetchAdminGeneralSettings.rejected, (state, action: any) => { 
        state.settingsLoading = false; 
        state.settingsError = action.payload as string; 
      });

    builder
      .addCase(fetchAdminGeneralSettingsById.pending, (state) => { 
        state.settingsLoading = true; 
        state.settingsError = null; 
      })
      .addCase(fetchAdminGeneralSettingsById.fulfilled, (state, action: PayloadAction<AdminGeneralSettings>) => { 
        state.settingsLoading = false; 
        state.currentSettings = action.payload; 
      })
      .addCase(fetchAdminGeneralSettingsById.rejected, (state, action: any) => { 
        state.settingsLoading = false; 
        state.settingsError = action.payload as string; 
      });

    // Create Extra Reducers
    builder
      .addCase(createAdminGeneralSettings.pending, (state) => { 
        state.settingsLoading = true; 
        state.settingsError = null; 
      })
      .addCase(createAdminGeneralSettings.fulfilled, (state, action: PayloadAction<AdminGeneralSettings>) => { 
        state.settingsLoading = false; 
        state.settings.unshift(action.payload); 
        state.settingsTotalCount = (state.settingsTotalCount || 0) + 1; 
        state.latestSettings = action.payload; // Update latest settings
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(createAdminGeneralSettings.rejected, (state, action: any) => { 
        state.settingsLoading = false; 
        state.settingsError = action.payload as string; 
      });

    // Update Extra Reducers
    builder
      .addCase(updateAdminGeneralSettings.pending, (state) => { 
        state.settingsLoading = true; 
        state.settingsError = null; 
      })
      .addCase(updateAdminGeneralSettings.fulfilled, (state, action: PayloadAction<AdminGeneralSettings>) => {
        state.settingsLoading = false;
        const index = state.settings.findIndex(x => x.id === action.payload.id);
        if (index !== -1) state.settings[index] = action.payload;
        if (state.currentSettings?.id === action.payload.id) state.currentSettings = action.payload;
        state.latestSettings = action.payload; // Update latest settings
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(updateAdminGeneralSettings.rejected, (state, action: any) => { 
        state.settingsLoading = false; 
        state.settingsError = action.payload as string; 
      });

    // Delete Extra Reducers
    builder
      .addCase(deleteAdminGeneralSettings.pending, (state) => { 
        state.settingsLoading = true; 
        state.settingsError = null; 
      })
      .addCase(deleteAdminGeneralSettings.fulfilled, (state, action: PayloadAction<string>) => { 
        state.settingsLoading = false; 
        state.settings = state.settings.filter(x => x.id !== action.payload); 
        if (state.currentSettings?.id === action.payload) state.currentSettings = null; 
        if (state.latestSettings?.id === action.payload) state.latestSettings = null;
        state.settingsTotalCount = Math.max(0, (state.settingsTotalCount || 0) - 1); 
      })
      .addCase(deleteAdminGeneralSettings.rejected, (state, action: any) => { 
        state.settingsLoading = false; 
        state.settingsError = action.payload as string; 
      });
  }
});

export const { 
  clearLatestSettingsError,
  clearSettingsError, 
  clearCurrentSettings, 
  setSettingsSearchQuery,
  setFormDirty,
  clearAllSettings,
  updateLatestSettingsFromForm
} = adminGeneralSettingsSlice.actions;

export default adminGeneralSettingsSlice.reducer;

